import type {CSSProperties} from "react";
import {Controller, type UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {FormGroup, Input, Label} from "reactstrap";
import type {Step1Form} from "./Step1";

const SIRET_LENGTH = 14;

// Monospace + letter-spacing so the typed digits land exactly on the underscore
// slots of the underlay (both use this style).
const MASK_STYLE: CSSProperties = {
  fontFamily: "var(--bs-font-monospace, monospace)",
  letterSpacing: "0.3em",
};

type Props = {
  hookForm: UseFormReturn<Step1Form>;
  disabled: boolean;
};

/**
 * SIRET input for the auto-fill flow. The field shows a 14-character mask
 * (`______________`) whose underscores are replaced by the digits as the user
 * types, so they can see exactly how many characters are left. Digit-only entry,
 * capped at 14, with a success border once complete. Lookup feedback (loading,
 * match, errors) is rendered separately by `SiretLookupFeedback`. The plain
 * manual variant keeps the shared `Field`, so this is scoped to the feature.
 */
export function SiretField({hookForm, disabled}: Props) {
  const {t: translate} = useTranslation();
  const label = translate("myTourApp.platformUser.siret");

  return (
    <Controller
      name="siret"
      control={hookForm.control}
      render={({field, fieldState}) => {
        const value = field.value ?? "";
        const count = value.length;
        const complete = count === SIRET_LENGTH;
        const remaining = "_".repeat(SIRET_LENGTH - count);

        return (
          <FormGroup>
            <Label for="field-siret">{`${label} *`}</Label>
            <div style={{position: "relative"}}>
              <Input
                id="field-siret"
                data-testid="field-siret"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={SIRET_LENGTH}
                disabled={disabled}
                value={value}
                onBlur={field.onBlur}
                innerRef={field.ref}
                onChange={(e) => field.onChange(sanitizeSiret(e.target.value))}
                valid={complete && !fieldState.error}
                invalid={!!fieldState.error}
                style={{
                  ...MASK_STYLE,
                  position: "relative",
                  zIndex: 1,
                  backgroundColor: "transparent",
                }}
              />
              <div
                aria-hidden
                data-testid="siret-mask"
                className="form-control"
                style={{
                  ...MASK_STYLE,
                  position: "absolute",
                  inset: 0,
                  zIndex: 0,
                  border: "1px solid transparent",
                  backgroundColor: "transparent",
                  color: "var(--bs-secondary-color, #adb5bd)",
                  pointerEvents: "none",
                  whiteSpace: "pre",
                  overflow: "hidden",
                }}
              >
                {value}
                {remaining}
              </div>
            </div>
          </FormGroup>
        );
      }}
    />
  );
}

// SIRET is 14 digits — strip anything else and cap the length so the field can't
// overflow or hold letters.
function sanitizeSiret(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, SIRET_LENGTH);
}
